import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'

import { Button, Page, Spinner } from '../../../components'
import { images } from '../../../images'
import { AuthenticatedRoutable, IReservation } from '../../../models'
import { urls } from '../../../routing'
import { LiteralsService } from '../../../services'
import {
  fetchAreasAction,
  fetchMoreReservationsAction,
  fetchReservationsAction,
  resetNewReservationFieldsAction,
  setQrCodeAndDetails,
  setSelectedReservationModalAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import { Card } from './Card'
import StyledSection from './styles'
import { appsShowRefundsSectionSelected, appsStyleSelected } from '../../../helpers/app'
import { AnyAction } from 'redux'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  upcomingReservations: IReservation[]
  isFetchingReservations: boolean
  isFetchingMoreReservations: boolean
  isLoadMoreExist: boolean
  page: number
  size: number
  experienced: boolean
  userTownId: number
}

class Reservations extends AuthenticatedRoutable<IProps> {
  private get emptyReservations(): ReactNode {
    return (
      <div className="empty-reservations">
        <img src={ images['noReservations'] } alt={ LiteralsService.get('noReservations') }/>
        <h3>{ LiteralsService.get('noReservations', true) }</h3>
        <p>{ LiteralsService.get('noReservationsExplanation', true) }</p>
      </div>
    )
  }

  private get reservationsContent(): ReactNode {
    const { upcomingReservations, isFetchingReservations, isFetchingMoreReservations } = this.props
    
    if (isFetchingReservations) {
      return (
        <article className="spinner-container">
          <Spinner />
        </article>
      )
    } else {
      if (!upcomingReservations.length) return this.emptyReservations
      return (
        <>
          <div className='opacity-overlay'></div>
          {
            upcomingReservations.map((reservation, i) => (
              <Card
                key={ i }
                reservation={ reservation }
                onClick={ () => this.goToReservationDetails(reservation) }
                onFavoriteClick={ () => this.handleOpenFavoriteModal(reservation) }
                onQRClick={ () => this.handleOpenQRModal(reservation) }
              />
            ))
          }
          {
            isFetchingMoreReservations &&
            <article className="spinner-container more">
              <Spinner />
            </article>
          }
        </>
      )
    }
  }

  private get isUserExperienced(): boolean {
    const { experienced, userTownId } = this.props
    return experienced && !!userTownId
  }

  public componentDidMount(): void {
    const { dispatch, size } = this.props
    super.componentDidMount()
    dispatch(resetNewReservationFieldsAction())
    dispatch(fetchReservationsAction(size, 1))
    if (!this.isUserExperienced) dispatch(fetchAreasAction())
  }

  public render(): ReactNode {

    const { history } = this.props

    return (
      <Page className='authenticated reservations'>
        <StyledSection>
          <nav className="tabs">
            <div className="active">
              <span>{ LiteralsService.get('nextReservations', true) }</span>
            </div>
            <div className="divider" />
            <div onClick={ () => history.push(urls.pastReservations) }>
              <span>{ LiteralsService.get('pastReservations', true) }</span>
            </div>
            { appsShowRefundsSectionSelected?.show &&
              <>
                <div className="divider" />
                <div onClick={ () => history.push(urls.refundedReservations) }>
                  <span>{ LiteralsService.get('refunds', true) }</span>
                </div>
              </>
            }
          </nav>
          <div className="reservations" onScroll={this.handleScroll}>
            { this.reservationsContent }
          </div>
        </StyledSection>
        <Button
          title={ LiteralsService.get('newReservation') }
          icon="plusWhite"
          onPress={ this.goToNewReservation }
          className={ `floating-button ${appsStyleSelected?.style}` }
          filled
          upper
        />
      </Page>
    )
  }

  private goToNewReservation = () => {
    this.props.history.replace(urls.newReservationBusStopsStep)
  }

  private goToReservationDetails = (reservation: IReservation) => {
    this.props.history.push(
      `${ urls.reservationDetails }/${ reservation.id }`,
      { id: reservation.id, cancelled: reservation.cancelled, relatedId: reservation.relatedReservationId }
    )
  }

  private handleOpenFavoriteModal = (reservation?: IReservation) => {
    this.props.dispatch(setSelectedReservationModalAction(reservation ?? null))
  }

  private handleOpenQRModal = (reservation: IReservation) => {
    const { dispatch } = this.props
    dispatch(setQrCodeAndDetails(reservation))
  }

  private handleScroll = (e: any) => {
    const { dispatch, size, page, isLoadMoreExist, isFetchingMoreReservations } = this.props
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight;
    if (bottom && isLoadMoreExist && !isFetchingMoreReservations) {
      dispatch(fetchMoreReservationsAction(size, page))
    }
  }
}

const mapStateToProps = ({ reservations, userInfo }: IStoreState) => ({
  upcomingReservations: reservations.upComing,
  isFetchingReservations: reservations.isFetchingReservations,
  isFetchingMoreReservations: reservations.isFetchingMoreReservations,
  isLoadMoreExist: reservations.isLoadMoreExist,
  page: reservations.page,
  size: reservations.size,
  experienced: userInfo.experienced,
  userTownId: userInfo.town.id
})

export default withRouter(connectRedux(mapStateToProps, Reservations))
